import React, { useEffect, useId, useRef, useState, ChangeEvent } from 'react';
import { AlertCircle, ImageIcon, Loader, Upload, X } from 'lucide-react';
import { resolveImageContentType } from '../../../utils/storage';

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const MIN_IMAGE_DIMENSION = 600;

export interface ShopImageUploadValidated {
  file: File;
  dataUrl: string;
}

export type ShopImageUploadVariant = 'square' | 'reward-card';

export interface ShopImageUploadPreviewProps {
  variant: ShopImageUploadVariant;
  label: string;
  hint?: string;
  imageUrl?: string | null;
  isValidating?: boolean;
  isUploading?: boolean;
  error?: string;
  onValidatedImage?: (next: ShopImageUploadValidated) => void;
  onRemoveImage?: () => void;
  onImageError?: (message: string) => void;
  onImageLoadError?: () => void;
}

const validateMinDimensions = (width: number, height: number): string | null => {
  if (width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION) {
    return '이미지 크기는 가로·세로 각 600px 이상이어야 합니다';
  }
  return null;
};

const ShopImageUploadPreview: React.FC<ShopImageUploadPreviewProps> = ({
  variant,
  label,
  hint,
  imageUrl,
  isValidating = false,
  isUploading = false,
  error,
  onValidatedImage,
  onRemoveImage,
  onImageError,
  onImageLoadError,
}) => {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [internalError, setInternalError] = useState('');
  const [internalValidating, setInternalValidating] = useState(false);
  const [isBroken, setIsBroken] = useState(false);

  const displayUrl =
    imageUrl && !isBroken && !(isValidating || isUploading || internalValidating)
      ? imageUrl
      : null;
  const displayError = error || internalError;
  const busy = isValidating || isUploading || internalValidating;

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fail = (message: string) => {
    setInternalError(message);
    onImageError?.(message);
    setInternalValidating(false);
    resetInput();
  };

  const processFile = (file: File) => {
    const contentType = resolveImageContentType(file);
    if (!contentType) {
      fail('PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다');
      return;
    }

    if (file.size >= MAX_IMAGE_SIZE_BYTES) {
      fail('파일 크기는 10MB 미만이어야 합니다');
      return;
    }

    setIsBroken(false);
    setInternalError('');
    setInternalValidating(true);

    const img = document.createElement('img');
    const reader = new FileReader();
    let isValidated = false;

    const validateDimensions = (dataUrl: string) => {
      if (isValidated) return;
      isValidated = true;

      const dimensionError = validateMinDimensions(img.naturalWidth, img.naturalHeight);

      if (dimensionError) {
        fail(dimensionError);
        return;
      }

      setInternalValidating(false);
      setInternalError('');
      onValidatedImage?.({ file, dataUrl });
    };

    reader.onerror = () => fail('이미지를 읽을 수 없습니다');

    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (typeof dataUrl !== 'string') {
        fail('이미지를 읽을 수 없습니다');
        return;
      }

      img.onload = () => validateDimensions(dataUrl);
      img.onerror = () => fail('이미지를 불러올 수 없습니다');
      img.src = dataUrl;

      if (img.complete && img.naturalWidth > 0) {
        validateDimensions(dataUrl);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setInternalError('');
    setIsBroken(false);
    resetInput();
    onRemoveImage?.();
  };

  useEffect(() => {
    setIsBroken(false);
  }, [imageUrl]);

  const conditionText =
    variant === 'square'
      ? '600px↑ · 권장 1:1 · 10MB↓'
      : '600px↑ · 권장 16:9 · 10MB↓';

  const helperText =
    variant === 'square'
      ? 'PNG, JPG, JPEG, WEBP · 가로·세로 각 600px 이상 · 권장 1:1 · 10MB 미만'
      : 'PNG, JPG, JPEG, WEBP · 가로·세로 각 600px 이상 · 권장 16:9 · 10MB 미만';

  const previewClassName =
    variant === 'square'
      ? 'aspect-square w-full max-w-[280px] mx-auto rounded-3xl'
      : 'aspect-[16/9] w-full rounded-2xl';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 sm:p-6 h-full">
      <h3 className="text-base font-semibold text-gray-900 mb-1">{label}</h3>
      {hint && <p className="text-xs text-gray-500 mb-4">{hint}</p>}
      {!hint && <div className="mb-4" />}

      <div
        className="relative"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!busy) setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
          if (busy) return;
          const file = e.dataTransfer.files?.[0];
          if (!file) return;
          processFile(file);
        }}
      >
        {!displayUrl || busy ? (
          <label
            htmlFor={inputId}
            className={`flex flex-col items-center justify-center border-2 border-dashed cursor-pointer transition-colors ${previewClassName} ${
              displayError
                ? 'border-red-300 bg-red-50'
                : isDragging
                  ? 'border-gray-500 bg-gray-100'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
            } ${busy ? 'pointer-events-none opacity-70' : ''}`}
          >
            {isUploading ? (
              <>
                <Loader className="w-8 h-8 text-gray-400 animate-spin mb-2" />
                <span className="text-xs text-gray-500 text-center px-2">업로드 중...</span>
              </>
            ) : busy ? (
              <>
                <Loader className="w-8 h-8 text-gray-400 animate-spin mb-2" />
                <span className="text-xs text-gray-500 text-center px-2">확인 중...</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-xs text-gray-500 text-center leading-tight px-2">
                  클릭 또는 드롭
                </span>
                <span className="text-[10px] text-gray-400 text-center leading-tight px-2 mt-1">
                  {conditionText}
                </span>
              </>
            )}
          </label>
        ) : (
          <div
            className={`relative ${previewClassName} ${
              isDragging ? 'ring-2 ring-gray-400' : ''
            }`}
          >
            <label
              htmlFor={inputId}
              className="block w-full h-full overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer"
              title="다른 이미지 선택"
            >
              <img
                src={displayUrl}
                alt={`${label} 미리보기`}
                className="w-full h-full object-cover"
                onError={() => {
                  setIsBroken(true);
                  onImageLoadError?.();
                }}
              />
            </label>
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 z-10 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
              aria-label="이미지 삭제"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
          onChange={handleInputChange}
          className="hidden"
          disabled={busy}
        />
      </div>

      {displayError && (
        <p className="mt-3 text-sm text-red-500 flex items-start">
          <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
          {displayError}
        </p>
      )}

      {!displayUrl && !displayError && (
        <p className="mt-3 text-xs text-gray-400 flex items-center">
          <ImageIcon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
          {helperText}
        </p>
      )}
    </div>
  );
};

export default ShopImageUploadPreview;
